import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useSEO } from '../../hooks/useSEO';
import { articlesApi } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { ArrowLeft, Calendar, Eye, Share2, MapPin, User } from 'lucide-react';
import { toast } from 'sonner';

export const ArticleDetailPage = () => {
  const { slug } = useParams();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadArticle();
  }, [slug]);

  const loadArticle = async () => {
    setLoading(true);
    try {
      const response = await articlesApi.get(slug);
      setArticle(response.data);
    } catch (error) {
      console.error('Failed to load article:', error);
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  };

  const title = article ? (language === 'ar' && article.title_ar ? article.title_ar : article.title) : '';
  const content = article ? (language === 'ar' && article.content_ar ? article.content_ar : article.content) : '';

  useSEO({
    title: article ? `${title} | Beytam` : 'Beytam',
    description: article ? (article.excerpt || title) : '',
    ogTitle: article ? `${title} | Beytam` : 'Beytam',
    ogDescription: article ? (article.excerpt || title) : '',
    ogImage: article?.image || undefined,
    ogType: 'article',
    author: article?.author_name,
    canonical: window.location.href,
  });

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: article?.title,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success(language === 'fr' ? 'Lien copié !' : 'تم نسخ الرابط!');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'ar' ? 'ar-DZ' : 'fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Skeleton className="h-8 w-32 mb-6" />
        <Skeleton className="aspect-video rounded-xl mb-8" />
        <Skeleton className="h-12 w-3/4 mb-4" />
        <Skeleton className="h-6 w-1/2 mb-8" />
        <div className="space-y-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      </div>
    );
  }

  if (!article) return null;

  return (
    <div className="min-h-screen bg-background" data-testid="article-detail-page">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Back button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/blog')}
          className="mb-6 gap-2"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('common_back')}
        </Button>

        {/* Hero image */}
        <div className="aspect-video rounded-xl overflow-hidden mb-8">
          <img
            src={article.image || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200'}
            alt={title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <Badge className="bg-primary-500 text-white">{article.category}</Badge>
          <Badge variant="secondary" className="flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {article.city}
          </Badge>
          {article.tags?.map((tag) => (
            <Badge key={tag} variant="outline">{tag}</Badge>
          ))}
        </div>

        {/* Title */}
        <h1 className="font-manrope text-3xl md:text-4xl font-bold text-slate-900 mb-4">
          {title}
        </h1>

        {/* Author & date */}
        <div className="flex items-center justify-between mb-8 pb-8 border-b border-slate-200">
          <div className="flex items-center gap-4 text-sm text-slate-600">
            <span className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {article.author_name}
            </span>
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {formatDate(article.created_at)}
            </span>
            <span className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              {article.views} {language === 'fr' ? 'vues' : 'مشاهدة'}
            </span>
          </div>
          <Button variant="outline" size="sm" onClick={handleShare} className="gap-2">
            <Share2 className="w-4 h-4" />
            {language === 'fr' ? 'Partager' : 'مشاركة'}
          </Button>
        </div>

        {/* Social Share Buttons */}
        <div className="flex items-center gap-3 mb-8" data-testid="social-share">
          <span className="text-sm text-slate-500">{language === 'fr' ? 'Partager :' : 'مشاركة :'}</span>
          <a
            href={`https://wa.me/?text=${encodeURIComponent(title + ' - ' + window.location.href)}`}
            target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-full text-sm font-medium transition-colors"
            data-testid="share-whatsapp"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            WhatsApp
          </a>
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
            target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-full text-sm font-medium transition-colors"
            data-testid="share-facebook"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            Facebook
          </a>
        </div>

        {/* Content */}
        <article className="prose prose-slate max-w-none">
          {content.includes('<') ? (
            <div className="text-slate-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: content }} />
          ) : (
            <div className="text-slate-700 leading-relaxed whitespace-pre-line">{content}</div>
          )}
        </article>

        {/* CTA */}
        <Card className="mt-12 bg-primary-50 border-0">
          <CardContent className="p-8 text-center">
            <h3 className="font-manrope text-xl font-bold text-slate-900 mb-2">
              {language === 'fr' 
                ? `Vous cherchez un bien à ${article.city} ?`
                : `هل تبحث عن عقار في ${article.city}؟`}
            </h3>
            <p className="text-slate-600 mb-4">
              {language === 'fr'
                ? 'Découvrez toutes les annonces disponibles'
                : 'اكتشف جميع الإعلانات المتاحة'}
            </p>
            <Link to={`/search?city=${article.city}`}>
              <Button className="bg-primary-500">
                {language === 'fr' ? 'Voir les annonces' : 'عرض الإعلانات'}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
